import { Dispatch, SetStateAction, useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import CheckIcon from "../../icons/check.icon";
import CloseIcon from "../../icons/iconClose.comp";
import PencilIcon from "../../icons/pencil.icon";
import { selectCss } from "../reuseables/native.comp";

type Mode = "display" | "update" | "updating";

interface LabelAndValue {
  value: string;
  label: string;
  css: string;
}

interface Props {
  currentValue: string;
  options: LabelAndValue[];
  inputType?: "select" | "text" | "date";
  onUpdate: (value: string) => Promise<void>;
}

export default function UpdateBadge({ currentValue, options, inputType = "select", onUpdate }: Props) {
  const [mode, setMode] = useState<Mode>("display");

  switch (mode) {
    case "display":
      return <OrderStatusDisplay currentValue={currentValue} options={options} setMode={setMode} />;
    case "update":
      return (
        <OrderStatusEdit
          currentValue={currentValue}
          options={options}
          setMode={setMode}
          onUpdate={onUpdate}
          inputType={inputType}
        />
      );
    default:
      return null;
  }
}

function OrderStatusDisplay({
  currentValue,
  options,
  setMode,
}: {
  currentValue: string;
  options: LabelAndValue[];
  setMode: Dispatch<SetStateAction<Mode>>;
}) {
  const currentItem = options.find((i) => i.value === currentValue);
  return (
    <>
      <div
        className={
          "inline-block px-2 py-1 text-sm font-medium text-center rounded-lg focus:ring-4 focus:outline-none " + currentItem?.css
        }
      >
        {currentItem?.label}
      </div>
      <button data-role="update" className="cursor-pointer" onClick={() => setMode("update")}>
        <PencilIcon className="h-4 w-4 inline ml-2" />
      </button>
    </>
  );
}

interface FormData {
  selectValue: string;
}

interface OrderStatusEditProps {
  currentValue: string;
  options: LabelAndValue[];
  setMode: Dispatch<SetStateAction<Mode>>;
  onUpdate: (value: string) => Promise<void>;
  inputType: "select" | "text" | "date";
}

function OrderStatusEdit({ currentValue, options, setMode, onUpdate, inputType }: OrderStatusEditProps) {
  const methods = useForm<FormData>({
    defaultValues: {
      selectValue: currentValue,
    },
  });

  return (
    <>
      <UpdateInput options={options} methods={methods} inputType={inputType} />
      <Buttons currentValue={currentValue} methods={methods} setMode={setMode} onUpdate={onUpdate} />
    </>
  );
}

const UpdateInput = ({
  options,
  methods,
  inputType,
}: {
  options: LabelAndValue[];
  methods: UseFormReturn<FormData>;
  inputType: "select" | "text" | "date";
}) => {
  if (inputType === "text" || inputType === "date") {
    return <input {...methods.register("selectValue")} type={inputType} />;
  }

  return (
    <select className={`${selectCss} ml-2`} {...methods.register("selectValue")}>
      {options.map((item) => (
        <option key={item.value} value={item.value}>
          {item.label}
        </option>
      ))}
    </select>
  );
};

const Buttons = ({
  currentValue,
  methods,
  setMode,
  onUpdate,
}: {
  currentValue: string;
  methods: UseFormReturn<FormData, any>;
  setMode: Dispatch<SetStateAction<Mode>>;
  onUpdate: (value: string) => Promise<void>;
}) => {
  const selectingValue = useWatch({
    control: methods.control,
    name: "selectValue",
  });

  async function handleUpdate() {
    setMode("updating");
    await onUpdate(selectingValue);
    setMode("display");
  }

  return (
    <div className="inline-block ml-2">
      {currentValue !== selectingValue && (
        <button className="button-yes cursor-pointer" onClick={handleUpdate}>
          <CheckIcon className="h-4 w-4 text-green-600" />
        </button>
      )}
      <button className="button-close curor-pointer" onClick={() => setMode("display")}>
        <CloseIcon className="h-4 w-4 text-red-400" />
      </button>
    </div>
  );
};
